import {
  Content,
  Overlay,
  Portal,
  Root,
  Trigger,
  Title,
  Close,
} from '@radix-ui/react-dialog';
import cl from 'clsx';

import * as Contract from '@synapex/shared2/gateway-rest-api';
import CloseIcon from '@synapex/frontend-shared/icons/close-12.svg?react';

import { Button } from '@/true-fsd/shared/ui/ButtonLink';
import { CopyLink } from '@/true-fsd/shared/ui/CopyLink';
import classes from './CreateInvite.Content.module.scss';
import dialogClasses from './CreateInvite.Dialog.module.scss';
import { telegramGetBotStartUrl } from '@synapex/shared2/bot-start';
import * as BotStart from '@synapex/shared2/bot-start';

import { pickDto } from '@/shared/api';
import { createInvite } from '@/true-fsd/data/advertiser-account/api-client';
import { CSSProperties, ReactNode, useState } from 'react';
import { Checkboxes } from './Checkboxes';
import { LabeledCheckBox } from './LabeledCheckBox';
import { useCheckboxes } from './useCheckboxes';

export type CreateInviteDialogProps = {
  advertiser_account_id: number;
  children: ReactNode;
};
export const CreateInviteDialog = ({
  advertiser_account_id,
  children,
}: CreateInviteDialogProps) => {
  const [newInvite, setNewInvite] =
    useState<Contract.AdvertiserAccount.User.InviteReply | null>(null);

  return (
    <Root modal>
      <Trigger asChild>{children}</Trigger>
      <Portal>
        <Overlay className={dialogClasses.overlay} />
        <Content
          aria-describedby={undefined}
          className={cl(dialogClasses.content, classes.root)}
        >
          <Close className={dialogClasses.close}>
            <CloseIcon className={dialogClasses.closeIcon} />
          </Close>
          <Title className={classes.title}>Добавить пользователя</Title>

          {newInvite ? (
            <DisplayInvite newInvite={newInvite} />
          ) : (
            <CreateInvite
              advertiser_account_id={advertiser_account_id}
              onSuccess={setNewInvite}
            />
          )}
        </Content>
      </Portal>
    </Root>
  );
};

const STYLE = {
  button: { height: 49, fontSize: 14 } satisfies CSSProperties,
  bottomLabeledCheckbox: { gap: 6 } satisfies CSSProperties,
};

type CreateInviteProps = {
  advertiser_account_id: number;
  onSuccess: (result: Contract.AdvertiserAccount.User.InviteReply) => void;
};
const CreateInvite = ({
  advertiser_account_id,
  onSuccess,
}: CreateInviteProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const [one_time, setOneTime] = useState(false);

  const [state, dispatch] = useCheckboxes();

  const makeDto = (): Contract.AdvertiserAccount.User.CreateInviteInputDto => {
    return pickDto(Contract.AdvertiserAccount.User.CreateInviteInputDto, {
      one_time,
      ...state,
    });
  };

  const onSubmit = () => {
    setIsLoading(true);
    void createInvite({ advertiser_account_id }, makeDto())
      .then(onSuccess)
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      <Checkboxes
        title={'Выберите настройки доступа:'}
        state={state}
        onCheckedChange={dispatch}
        disabled={isLoading}
      />
      <div className={classes.bottom}>
        <Button
          className={classes.button}
          style={STYLE.button}
          variant={'primary'}
          loading={isLoading}
          onClick={onSubmit}
        >
          Создать пригласительную ссылку
        </Button>
        <LabeledCheckBox
          checked={one_time}
          onCheckedChange={setOneTime}
          disabled={isLoading}
          style={STYLE.bottomLabeledCheckbox}
        >
          Сделать ссылку одноразовой
        </LabeledCheckBox>
      </div>
    </>
  );
};

type DisplayInviteProps = {
  newInvite: Contract.AdvertiserAccount.User.InviteReply;
};
const DisplayInvite = ({ newInvite }: DisplayInviteProps) => {
  const link = telegramGetBotStartUrl(
    import.meta.env.VITE_BOT_USERNAME,
    BotStart.Invite,
    { inviteToken: newInvite.token },
  );
  return (
    <>
      <Checkboxes
        title={'Инвайт-ссылка создана. Скопируйте ее и передайте пользователю'}
        state={newInvite}
        disabled={true}
      />
      <CopyLink link={link.href} />
      {newInvite.one_time && <div>Ссылка является одноразовой</div>}
    </>
  );
};
